/* eslint-disable no-undef */
import React, { useState, useEffect } from "react";

import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Text,
  Input,
  Field,
  makeStyles,
  tokens,
  Badge,
  MessageBar,
  MessageBarBody,
  Spinner,
} from "@fluentui/react-components";

import {
  CopyRegular,
  ShieldKeyholeRegular,
  CheckmarkCircleRegular,
} from "@fluentui/react-icons";

const useStyles = makeStyles({
  dialogSurface: {
    width: "720px",
    maxWidth: "95vw",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
    marginTop: "12px",
  },
  section: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    padding: "18px",
    background: tokens.colorNeutralBackground1,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "12px",
  },
  title: {
    fontWeight: 600,
    fontSize: "16px",
  },
  credentialsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "16px",
  },
  inputRow: {
    display: "flex",
    gap: "10px",
    alignItems: "end",
  },
  helperText: {
    color: tokens.colorNeutralForeground3,
    fontSize: "13px",
  },
  successBox: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: tokens.colorPaletteGreenForeground1,
    fontWeight: 600,
  },
});

export default function ScvCredentialsDialog({
  open,
  onOpenChange,
  onGenerateCredentials,
}) {
  const styles = useStyles();
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
    expiresAt: null,
  });

  useEffect(() => {
    // Reset dialog state whenever dialog closes
    if (!open) {
      setGenerated(false);

      setCredentials({
        username: "",
        password: "",
        expiresAt: null,
      });

      setLoading(false);
    }
  }, [open]);

  const handleGenerate = async () => {
    try {
      setLoading(true);

      const response = await onGenerateCredentials?.();

      // If the caller didn't return a payload (e.g. errored and surfaced its
      // own toast), keep the dialog in its pre-generated state.
      if (!response?.username || !response?.password) {
        return;
      }

      setCredentials({
        username: response.username,
        password: response.password,
        expiresAt: response.expiresAt || null,
      });

      setGenerated(true);
    } catch (err) {
      // Caller is expected to surface a user-facing error; we just log here.
      console.error("Failed to generate credentials", err);
    } finally {
      setLoading(false);
    }
  };

  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle>Generate Salesforce Voice (SCV) Link Credentials</DialogTitle>

          <DialogContent className={styles.content}>
            <MessageBar intent="warning">
              <MessageBarBody>
                Credentials are shown only once. The password/secret cannot be
                retrieved again after closing this dialog.
              </MessageBarBody>
            </MessageBar>

            {!generated ? (
              <div className={styles.section}>
                <div className={styles.header}>
                  <ShieldKeyholeRegular fontSize={22} />

                  <Text className={styles.title}>
                    Generate One-Time Credentials
                  </Text>
                </div>

                <Text className={styles.helperText}>
                  Generate temporary credentials for linking Salesforce call
                  center integrations securely.
                </Text>

                <DialogActions style={{ marginTop: 18 }}>
                  <Button
                    appearance="secondary"
                    onClick={() => onOpenChange?.({}, { open: false })}
                  >
                    Cancel
                  </Button>

                  <Button
                    appearance="primary"
                    onClick={handleGenerate}
                    disabled={loading}
                    icon={loading ? <Spinner size="tiny" /> : null}
                  >
                    {loading ? "Generating..." : "Generate Credentials"}
                  </Button>
                </DialogActions>
              </div>
            ) : (
              <div className={styles.section}>
                <div className={styles.header}>
                  <CheckmarkCircleRegular
                    color={tokens.colorPaletteGreenForeground1}
                    fontSize={22}
                  />

                  <Text className={styles.title}>
                    Credentials Generated Successfully
                  </Text>
                </div>

                <Badge appearance="filled" color="danger">
                  Visible Only Once
                </Badge>

                <div
                  className={styles.credentialsGrid}
                  style={{ marginTop: 18 }}
                >
                  {/* USERNAME */}
                  <Field label="Username">
                    <div className={styles.inputRow}>
                      <Input value={credentials.username} readOnly />

                      <Button
                        icon={<CopyRegular />}
                        appearance="subtle"
                        onClick={() => copyText(credentials.username)}
                      >
                        Copy
                      </Button>
                    </div>
                  </Field>

                  {/* PASSWORD */}
                  <Field label="One-Time Password / Secret">
                    <div className={styles.inputRow}>
                      <Input
                        type="password"
                        value={credentials.password}
                        readOnly
                      />

                      <Button
                        icon={<CopyRegular />}
                        appearance="subtle"
                        onClick={() => copyText(credentials.password)}
                      >
                        Copy
                      </Button>
                    </div>
                  </Field>

                  {credentials.expiresAt && (
                    <Field label="Expires At">
                      <Input
                        value={new Date(credentials.expiresAt).toLocaleString()}
                        readOnly
                      />
                    </Field>
                  )}
                </div>

                <div style={{ marginTop: 18 }}>
                  <Text className={styles.helperText}>
                    Store these credentials securely before closing this dialog.
                  </Text>
                </div>

                <DialogActions style={{ marginTop: 20 }}>
                  <Button
                    appearance="primary"
                    onClick={() => onOpenChange?.({}, { open: false })}
                  >
                    Done
                  </Button>
                </DialogActions>
              </div>
            )}
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}